const toArray = (value) => (Array.isArray(value) ? value : []);

const pickString = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value);
    }
  }
  return "";
};

const DEFAULT_UC_CHECKS = [""];

const EMPTY_UC_CHECKS = [""];

const DEFAULT_CERTIFICATION_TEXT =
  "Certified that I have satisfied that the conditions on which the grants-in-aid was sanctioned have been duly fulfilled and that I have exercised the necessary checks to see that the money was actually utilized for the purpose for which it was sanctioned.";

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return String(dateStr);
  return date.toLocaleDateString("en-GB");
};

const formatLetterNoAndDate = (orderNo, orderDate) => {
  const order = pickString(orderNo);
  const date = formatDisplayDate(orderDate);

  if (order && date) {
    return `${order} dated ${date}`;
  }

  return order || date || "-";
};

const normalizeTableRows = (rows = []) =>
  toArray(rows).map((row, index) => ({
    srNo: pickString(row.srNo, row.sr_no, row.serial_no, index + 1),
    letterNoAndDate: pickString(
      row.letterNoAndDate,
      row.letter_no_and_date,
      row.letter_no,
      row.allocation_order
    ),
    subHeadName: pickString(row.subHeadName, row.sub_head_name, row.subhead_name),
    amount: Number(row.amount ?? row.expenditure ?? 0),
  }));

const normalizeChecks = (checks) => {
  const list = toArray(checks).map((item) => String(item ?? ""));

  return list.length > 0 ? list : [""];
};

const buildTableRowsFromScheme = (scheme = {}) => {
  const rows = [];

  toArray(scheme.budgets).forEach((budget) => {
    rows.push({
      letterNoAndDate: formatLetterNoAndDate(budget.order_no, budget.order_date),
      subHeadName: pickString(budget.subhead_name, budget.sub_head_name) || "-",
      amount: Number(budget.amount || 0),
    });
  });

  toArray(scheme.reappropriations).forEach((reappropriation) => {
    rows.push({
      letterNoAndDate: formatLetterNoAndDate(
        reappropriation.order_no,
        reappropriation.order_date
      ),
      subHeadName:
        pickString(reappropriation.subhead_name, reappropriation.sub_head_name) ||
        "Reappropriation",
      amount: Number(reappropriation.amount || 0),
    });
  });

  return normalizeTableRows(rows);
};

const buildSingleSchemeDraft = (
  scheme,
  {
    selectedFy,
    selectedMonthLabel = "-",
    departmentName = "",
    ucType = "",
    projectName = "",
  } = {}
) => {
  const schemeName = pickString(scheme.scheme_name, scheme.schemeName);
  const grantCode = pickString(scheme.grant_code, scheme.grantCode);
  const deptName = pickString(
    scheme.department_name,
    scheme.departmentName,
    departmentName
  );
  const selectedFyLabel = pickString(selectedFy?.year) || "-";
  const tableRows = buildTableRowsFromScheme(scheme);
  const totalAmount = tableRows.reduce(
    (sum, row) => sum + Number(row.amount || 0),
    0
  );
  const certificationColumnText = pickString(scheme.content);

  return {
    schemeId: scheme.scheme_id ?? scheme.schemeId ?? null,
    formTitle: "FORM GFR 12-C",
    ruleReference: "(See Rule 239)",
    formSubtitle: "(Form of Utilization Certificate (For State Government))",
    formCondition: "(Where expenditure incurred by Govt, bodies only)",
    projectName: pickString(scheme.project_name, scheme.projectName, projectName),
    grantCode,
    schemeProjectName: schemeName,
    departmentName: deptName,
    selectedFy: selectedFyLabel,
    selectedMonth: selectedMonthLabel,
    schemeNameLine: schemeName ? `Scheme Name: ${schemeName}` : "",
    ucType,
    tableRows,
    totalAmount,
    certificationColumnText,
    certificationColumnParagraphs: certificationColumnText
      ? [certificationColumnText]
      : [],
    certificationText: DEFAULT_CERTIFICATION_TEXT,
    defaultChecks: [...DEFAULT_UC_CHECKS],
  };
};

export const getCertificateKey = (certificate, index = 0) =>
  String(certificate?.schemeId ?? certificate?.scheme_id ?? `scheme-${index}`);

export const buildChecksExercisedState = (certificates = []) => {
  const checksByScheme = {};

  toArray(certificates).forEach((certificate, index) => {
    const key = getCertificateKey(certificate, index);
    checksByScheme[key] = normalizeChecks(
      certificate.defaultChecks || certificate.default_checks || DEFAULT_UC_CHECKS
    );
  });

  return checksByScheme;
};

export const normalizeUtilizationUcApiSchemes = (schemes = [], context = {}) =>
  toArray(schemes).map((scheme) => buildSingleSchemeDraft(scheme, context));

export const normalizeUtilizationUcReportResponse = (
  apiResult,
  {
    selectedFy,
    selectedMonthLabel = "-",
    departmentName = "",
    ucType = "",
  } = {}
) => {
  if (!apiResult) return null;

  if (apiResult.certificates && Array.isArray(apiResult.certificates)) {
    return {
      ...apiResult,
      defaultChecks: normalizeChecks(
        apiResult.defaultChecks || apiResult.default_checks
      ),
    };
  }

  if (apiResult.formTitle && Array.isArray(apiResult.tableRows)) {
    return {
      certificates: [
        {
          ...apiResult,
          defaultChecks: normalizeChecks(apiResult.defaultChecks),
        },
      ],
      defaultChecks: normalizeChecks(apiResult.defaultChecks),
      selectedFy: pickString(apiResult.selectedFy, apiResult.selected_fy, selectedFy?.year),
      selectedMonth: pickString(
        apiResult.selectedMonth,
        apiResult.selected_month,
        selectedMonthLabel
      ),
      ucType: pickString(apiResult.ucType, apiResult.uc_type, ucType),
    };
  }

  if (Array.isArray(apiResult)) {
    const certificates = normalizeUtilizationUcApiSchemes(apiResult, {
      selectedFy,
      selectedMonthLabel,
      departmentName,
      ucType,
      projectName: "",
    });

    if (!certificates.length) return null;

    return {
      certificates,
      defaultChecks: [...DEFAULT_UC_CHECKS],
      selectedFy: pickString(selectedFy?.year) || "-",
      selectedMonth: selectedMonthLabel,
      ucType,
    };
  }

  // Shape: { status, result: [...] } from backend
  if (apiResult?.result && Array.isArray(apiResult.result)) {
    const certificates = normalizeUtilizationUcApiSchemes(apiResult.result, {
      selectedFy,
      selectedMonthLabel,
      departmentName,
      ucType,
      projectName: pickString(apiResult.project_name, apiResult.projectName),
    });

    if (!certificates.length) return null;

    return {
      certificates,
      defaultChecks: [...DEFAULT_UC_CHECKS],
      selectedFy: pickString(selectedFy?.year) || "-",
      selectedMonth: selectedMonthLabel,
      ucType,
      projectName: pickString(apiResult.project_name, apiResult.projectName),
    };
  }

  const selectedFyLabel =
    pickString(
      apiResult.selected_fy,
      apiResult.selectedFy,
      selectedFy?.year
    ) || "-";

  const schemeProjectName = pickString(
    apiResult.scheme_project_name,
    apiResult.schemeProjectName,
    departmentName
  );

  const tableRows = normalizeTableRows(
    apiResult.table_rows || apiResult.tableRows
  );

  const totalAmount = Number(
    apiResult.total_amount ??
      apiResult.totalAmount ??
      tableRows.reduce((sum, row) => sum + Number(row.amount || 0), 0)
  );

  const singleDraft = {
    schemeId: apiResult.scheme_id ?? apiResult.schemeId ?? null,
    formTitle: pickString(apiResult.form_title, apiResult.formTitle, "FORM GFR 12-C"),
    ruleReference: pickString(
      apiResult.rule_reference,
      apiResult.ruleReference,
      "(See Rule 239)"
    ),
    formSubtitle: pickString(
      apiResult.form_subtitle,
      apiResult.formSubtitle,
      "(Form of Utilization Certificate (For State Government))"
    ),
    formCondition: pickString(
      apiResult.form_condition,
      apiResult.formCondition,
      "(Where expenditure incurred by Govt, bodies only)"
    ),
    schemeProjectName,
    departmentName: pickString(
      apiResult.department_name,
      apiResult.departmentName,
      departmentName
    ),
    selectedFy: selectedFyLabel,
    selectedMonth: pickString(
      apiResult.selected_month,
      apiResult.selectedMonth,
      selectedMonthLabel
    ),
    schemeNameLine: pickString(
      apiResult.scheme_name_line,
      apiResult.schemeNameLine,
      schemeProjectName ? `Scheme Name: ${schemeProjectName}` : ""
    ),
    ucType: pickString(apiResult.uc_type, apiResult.ucType, ucType),
    tableRows,
    totalAmount,
    certificationColumnText: pickString(
      apiResult.certification_column_text,
      apiResult.certificationColumnText,
      apiResult.content
    ),
    certificationColumnParagraphs:
      apiResult.certification_column_paragraphs ||
      apiResult.certificationColumnParagraphs,
    certificationText: pickString(
      apiResult.certification_text,
      apiResult.certificationText,
      DEFAULT_CERTIFICATION_TEXT
    ),
    defaultChecks: normalizeChecks(
      apiResult.default_checks || apiResult.defaultChecks
    ),
  };

  return {
    certificates: [singleDraft],
    defaultChecks: singleDraft.defaultChecks,
    selectedFy: selectedFyLabel,
    selectedMonth: singleDraft.selectedMonth,
    ucType: singleDraft.ucType,
  };
};

export const buildUcReportRequestPayload = ({
  fyId,
  month,
  ucType,
  departmentId = "",
  expenseDateBasis = "",
}) => ({
  fy_id: fyId || "",
  month: month || "",
  uc_type: ucType || "",
  department_id: departmentId || "",
  expense_date_basis: expenseDateBasis || "",
});

export const buildUcVerifyPayload = ({
  fyId,
  month,
  ucType,
  checksExercised,
  otp = "",
  expenseDateBasis = "",
}) => ({
  fy_id: fyId || "",
  month: month || "",
  uc_type: ucType || "",
  checks_exercised: checksExercised || {},
  otp: otp || "",
  expense_date_basis: expenseDateBasis || "",
});

export const buildUcCertificateRequestPayload = ({
  fyId,
  month,
  monthLabel,
  ucType,
  viewType,
  departmentId,
  checksExercised,
  ucReportData,
  otp = "",
}) => {
  const certificates = toArray(ucReportData?.certificates).map((certificate, index) => {
    const certificateKey = getCertificateKey(certificate, index);

    return {
      ...certificate,
      checksExercised:
        checksExercised?.[certificateKey] ||
        certificate.checksExercised ||
        [...EMPTY_UC_CHECKS],
    };
  });

  return {
    fy_id: fyId || "",
    month: month || "",
    month_label: monthLabel || "",
    uc_type: ucType || "",
    view_type: viewType || "",
    department_id: departmentId || "",
    checks_exercised: checksExercised || {},
    uc_report: ucReportData
      ? {
          ...ucReportData,
          certificates,
        }
      : null,
    otp: otp || undefined,
  };
};
